import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, nextTick } from 'vue';
import BaseCard from "@/components/base/BaseCard.vue";
import SvgToken from "@/assets/icons/token.svg";
import BaseButton from "@/components-UI/BaseButton.vue";
import AppTableDesign from "@/components/base/table/AppTableDesign.vue";
import BaseSelect from '@/components-UI/BaseSelect.vue';
import SvgCopy from "@/assets/icons/copy.svg";
import { getUserTokensV3, createUserTokenV3, deleteUserTokenV3 } from "@/api/user";
import { translateAffiliateAdministrationTokenStatus } from "@/utils/translate";
import { showNotification, showServerError } from '@/utils';
import { PAGINATION_PER_PAGE } from "@/configs/global";
import { IUserActiveToken } from '@/api/types/user';
import BaseText from "@/components-UI/BaseText.vue";
import { useCopyText } from "@/utils/copyText";
import router from "@/router";

interface IFilterFields {
    id: number,
    value: string,
    items: {
        value: string | number,
        text: string,
    }[],
    placeholder: string,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ActiveTokensCardTable',
  props: {
    userId: null
  },
  setup(__props: any) {

const props = __props as {
    userId: number,
}


const loadingTokenTable = ref<boolean>(false);
const activeTokens = ref<IUserActiveToken[]>([]);
const totalTableElements = ref<number>(0);
const headers = ref([
    { text: 'ID', value: 'id', align: 'center', width: '60px' },
    { text: 'Тип токена', value: 'type', width: '80px' },
    { text: 'Статус', value: 'status', align: 'center', width: '80px' },
    { text: 'Токен', value: 'token', width: '210px', align: 'center' },
    { text: 'IP', value: 'ip', align: 'center' },
    { text: 'User-agent', value: 'user_agent' },
    { text: 'Дата создания', value: 'created_at', align: 'right', width: '90px' },
    { text: 'Действует до', value: 'expires_at', align: 'right', width: '90px' },
    { text: '', value: 'actions', align: 'center', width: '50px' },
]);
const timeData = ref([
    {
        slot: 'item.created_at',
        key: 'created_at',
        isLine: false,
    },
    {
        slot: 'item.expires_at',
        key: 'expires_at',
        isLine: false,
    },
]);
const actions = ref([
    {
        title: 'Разлогинить',
        color: '#FFFFFF',
        customIconClass: 'icon-path-fill',
        icon: 'SvgUserLogout',
        isTooltip: true,
        disabled: (item: any) => (item.status !== 'active'),
        callback: ({ id }: { id: number }): void => {
            logoutUser(id);
        },
    },
]);
const uniqueData = ref([
    { slot: 'item.token' },
    { slot: 'item.user_agent' },
]);
const filterFields = ref<IFilterFields[]>([
    {
        id: 1,
        value: router.currentRoute.query.status || 'active',
        items: [
            { value: 'all', text: 'Все статусы' },
            { value: 'active', text: 'Активен' },
            { value: 'expired', text: 'Не активен' },
        ],
        placeholder: 'Статус',
        update: async (event: string) => {
            filterFields.value[0].value = event;
            updateQuery({ status: event });
            await getActiveTokens();
        },
    },
    {
        id: 2,
        value: router.currentRoute.query.type || router.currentRoute.name === 'affiliate' ? 'user' : 'all',
        items: [
            { value: 'all', text: 'Все типы' },
            { value: 'api', text: 'API' },
            { value: 'user', text: 'User' },
        ],
        placeholder: 'Тип',
        update: async (event: string) => {
            filterFields.value[1].value = event;
            updateQuery({ type: event });
            await getActiveTokens();
        },
    },
]);
const statuses = ref([
    {
        slot: 'item.status',
        key: 'status',
        translate: translateAffiliateAdministrationTokenStatus,
    },
]);

const copyToken = (token: string): void => {
    useCopyText(token, 'Токен скопирован');
};

const updateQuery = (query: { offset?: number, status?: string, type?: string }): void => {
    router.push({
        query: {
            ...router.currentRoute.query,
            ...query,
        },
    });
};

const getPaginationData = async (offset: number): Promise<void> => {
    updateQuery({ offset });
    await getActiveTokens();
};

const createToken = async(): Promise<void> => {
    try {
        await createUserTokenV3({
            user_id: Number(props.userId),
            token_type: 'api',
        });
        showNotification('API-токен создан');
        await getActiveTokens();
        filterFields.value[1].update('all');
    } catch (err) {
        showServerError(err, 'Не удалось добавить токен');
    }
};

const logoutUser = async (id: number): Promise<void> => {
    loadingTokenTable.value = true;
    try {
        await deleteUserTokenV3({
            user_id: Number(props.userId),
            token_id: id,
        });
        await getActiveTokens();
    } catch (err) {
        showServerError(err, 'Пользователь не разлогинен');
    } finally {
        loadingTokenTable.value = false;
    }
};

const getActiveTokens = async (): Promise<void> => {
    try {
        const query = router.currentRoute.query;
        const params: any = {
            offset: query.offset,
            limit: PAGINATION_PER_PAGE,
        };
        if (query.type !== 'all') {
            params.type = query.type;
        }
        if (query.status !== 'all') {
            params.status = query.status;
        }

        const { data, meta } = await getUserTokensV3({
            user_id: Number(props.userId),
            params,
        });
        activeTokens.value = data;
        totalTableElements.value = meta!.total;
    } catch (e) {
        showServerError(e, 'Не удалось получить список токенов');
    }
};

onMounted(() => {
    nextTick(async () => {
        if( router.currentRoute.name === 'affiliate' ){
            updateQuery({ offset: 0, status: 'active', type: 'user' });
        }else{
            updateQuery({ offset: 0, status: 'active', type: 'all' });
        }

        await getActiveTokens();
    });
});

return { __sfc: true,props, loadingTokenTable, activeTokens, totalTableElements, headers, timeData, actions, uniqueData, filterFields, statuses, copyToken, updateQuery, getPaginationData, createToken, logoutUser, getActiveTokens, BaseCard, SvgToken, BaseButton, AppTableDesign, BaseSelect, SvgCopy, PAGINATION_PER_PAGE, BaseText }
}

})